import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Layout, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface HamburgerMenuProps {
  currentMode: 'kanban' | 'standup';
  onModeChange: (mode: 'kanban' | 'standup') => void;
  currentBoard?: any;
}

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({
  currentMode,
  onModeChange,
  currentBoard
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useAuth();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={toggleMenu}
        className="p-2 text-gray-600 hover:text-satsuma-600 transition-colors rounded-lg hover:bg-white/15 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
        title="Menu"
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            className="absolute top-full left-0 mt-2 w-64 sm:w-64 bg-white/95 backdrop-blur-md rounded-2xl border border-white/50 shadow-xl z-50 max-w-[calc(100vw-2rem)]"
          >
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3 px-3">View Mode</h3>
              
              <button
                onClick={() => {
                  onModeChange('kanban');
                  setIsOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                  currentMode === 'kanban'
                    ? 'bg-satsuma-100 text-satsuma-700'
                    : 'text-gray-700 hover:bg-white/15'
                }`}
              >
                <Layout size={18} />
                <div className="text-left">
                  <div className="font-medium">Product Boards</div>
                  <div className="text-xs text-gray-500">Kanban-style product tracking</div>
                </div>
              </button>

              <button
                onClick={() => {
                  onModeChange('standup');
                  setIsOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors mt-2 ${
                  currentMode === 'standup'
                    ? 'bg-satsuma-100 text-satsuma-700'
                    : 'text-gray-700 hover:bg-white/15'
                }`}
              >
                <Users size={18} />
                <div className="text-left">
                  <div className="font-medium">What Are We Doing?</div>
                  <div className="text-xs text-gray-500">Daily standup team updates</div>
                </div>
              </button>

              {currentBoard && (
                <div className="mt-4 pt-4 border-t border-white/20">
                  <div className="px-3 py-2">
                    <div className="text-xs text-gray-500">Current Board</div>
                    <div className="text-sm font-medium text-gray-800">{currentBoard.name}</div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HamburgerMenu;
